'use client';

import { useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/navigation';
import { useMediaQuery } from 'react-responsive';
import { breakpoints } from '@instamenu/ui-vanilla-extract';
import { useLoyaltyInit } from './use-loyalty-init';
import { LoyaltyCard3D } from './loyalty-card-3d';
import { makePath } from '../utils';
import {
  flipContainer,
  flipInner,
  flipInnerFlipped,
  flipFront,
  flipBack,
} from './loyalty-card-3d.css';
import { clsx } from 'clsx';

export const EnrollmentCard3D = observer(() => {
  const { checkout, loyalty, user } = useLoyaltyInit();
  const isDesktop = useMediaQuery({ query: breakpoints.tablet });
  const router = useRouter();

  const handleEnroll = useCallback(() => {
    if (!loyalty) return;
    loyalty.autoEnroll();
  }, [loyalty]);

  const handleCardClick = useCallback(() => {
    if (checkout?.order.restaurant.id) {
      router.push(
        makePath({
          restaurantId: checkout.order.restaurant.id,
          tableCode: checkout.order.table || undefined,
          menuId: checkout.menuId,
        }),
      );
    }
  }, [checkout?.menuId, checkout?.order.restaurant.id, checkout?.order.table, router]);

  const isEnrolled = !!loyalty?.status;
  const flipped = loyalty?.justEnrolled && isEnrolled;
  if (!user || !loyalty?.isActive) return null;
  if (isEnrolled && !loyalty.justEnrolled) return null;

  const program = loyalty.program;
  if (!program) return null;

  const programName = program.displayName ?? 'Rewards';
  const accentColor = checkout?.order.restaurant.restaurantGroup?.color ?? undefined;
  const isItemBased = loyalty.isItemBased;
  const earningThreshold = loyalty.earningThreshold;
  const cartPoints = loyalty.cartPoints;
  const title = `Join ${programName}`;
  const subtitle = isItemBased
    ? `Get a free item after ${earningThreshold} purchases`
    : 'Earn points on every order';

  const pointsNeeded = loyalty.earningThreshold - cartPoints;
  const pointsHint =
    !isItemBased && pointsNeeded > 0 ? `(${pointsNeeded} away from a free item)` : undefined;

  const action = isDesktop ? 'Click' : 'Tap';
  const rewardLabel =
    loyalty.rewardsAvailable > 0
      ? isItemBased
        ? `Free item ready! ${action} to get your reward`
        : `Redeem your points! ${action} to pick a free item`
      : "Welcome! You're now a member";

  return (
    <div className={flipContainer}>
      <div className={clsx(flipInner, flipped && flipInnerFlipped)}>
        <div className={flipFront}>
          <LoyaltyCard3D
            programName={title}
            pointsBalance={isItemBased ? 0 : cartPoints}
            accentColor={accentColor}
            isItemBased={isItemBased}
            earningThreshold={earningThreshold}
            progress={isItemBased ? 1 : 0}
            enrollment={{
              subtitle,
              ctaLabel: loyalty.enrolling ? 'Enrolling…' : 'Start earning rewards',
              onEnroll: handleEnroll,
              pointsHint,
            }}
          />
        </div>

        <div className={flipBack}>
          <LoyaltyCard3D
            programName={programName}
            rewardsAvailable={loyalty.rewardsAvailable}
            pointsBalance={loyalty.pointsBalance ?? (isItemBased ? 0 : cartPoints)}
            accentColor={accentColor}
            isItemBased={isItemBased}
            earningThreshold={earningThreshold}
            progress={loyalty.progress ?? (isItemBased ? 1 : 0)}
            rewardLabel={rewardLabel}
            onClick={handleCardClick}
          />
        </div>
      </div>
    </div>
  );
});
